import { useState, useEffect } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table'
import { Link } from 'react-router-dom'
import { Eye, Pencil, Search, X, ArrowUpDown, ArrowUp, ArrowDown, Filter } from 'lucide-react'
import type { Client, ClientFilters } from '../types'

const columnHelper = createColumnHelper<Client>()

interface LocalFilters {
  search: string
  id_branch_office: string
  last_known_category: string
  gender: string
  created_from: string
  created_to: string
}

function filtersToLocal(filters: ClientFilters): LocalFilters {
  return {
    search: filters.search ?? '',
    id_branch_office: filters.id_branch_office !== undefined ? String(filters.id_branch_office) : '',
    last_known_category: filters.last_known_category ?? '',
    gender: filters.gender ?? '',
    created_from: filters.created_from ?? '',
    created_to: filters.created_to ?? '',
  }
}

function formatRegisteredDate(value: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

interface ClientsTableProps {
  data: Client[]
  total: number
  filters: ClientFilters
  hasActiveFilters: boolean
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onApplyFilters: (filters: Partial<ClientFilters>) => void
  onSortingChange: (sortBy?: string, sortOrder?: 'asc' | 'desc') => void
  onClearFilters: () => void
  isLoading: boolean
  branchNames?: Record<number, string>
  categories?: string[]
}

export function ClientsTable({
  data,
  total,
  filters,
  hasActiveFilters,
  onPageChange,
  onLimitChange,
  onApplyFilters,
  onSortingChange,
  onClearFilters,
  isLoading,
  branchNames = {},
  categories = [],
}: ClientsTableProps) {
  const [localFilters, setLocalFilters] = useState<LocalFilters>(() => filtersToLocal(filters))
  const [showFilters, setShowFilters] = useState(false)

  // Sync local state when applied filters change externally (e.g. clearFilters)
  useEffect(() => {
    setLocalFilters(filtersToLocal(filters))
  }, [filters])

  const handleSearch = () => {
    onApplyFilters({
      search: localFilters.search.trim() || undefined,
    })
  }

  const handleApplyPanel = () => {
    onApplyFilters({
      search: localFilters.search.trim() || undefined,
      id_branch_office: localFilters.id_branch_office ? Number(localFilters.id_branch_office) : undefined,
      last_known_category: localFilters.last_known_category || undefined,
      gender: localFilters.gender || undefined,
      created_from: localFilters.created_from || undefined,
      created_to: localFilters.created_to || undefined,
    })
  }

  const handleClear = () => {
    onClearFilters()
  }

  const handleSort = (columnId: string) => {
    if (filters.sort_by === columnId) {
      if (filters.sort_order === 'desc') {
        onSortingChange(columnId, 'asc')
      } else {
        onSortingChange(undefined, undefined)
      }
    } else {
      onSortingChange(columnId, 'desc')
    }
  }

  function SortIcon({ columnId }: { columnId: string }) {
    if (filters.sort_by !== columnId) return <ArrowUpDown size={14} className="text-gray-400" />
    if (filters.sort_order === 'asc') return <ArrowUp size={14} className="text-primary-600" />
    return <ArrowDown size={14} className="text-primary-600" />
  }

  const columns = [
    columnHelper.accessor('id', {
      header: () => (
        <button onClick={() => handleSort('id')} className="flex items-center gap-1 hover:text-primary-600">
          ID <SortIcon columnId="id" />
        </button>
      ),
      size: 60,
    }),
    columnHelper.accessor('name', {
      header: () => (
        <button onClick={() => handleSort('name')} className="flex items-center gap-1 hover:text-primary-600">
          Nombre <SortIcon columnId="name" />
        </button>
      ),
      cell: ({ row }) => (
        <Link
          to={`/clients/${row.original.id}`}
          className="text-primary-600 hover:text-primary-800 hover:underline transition-colors"
        >
          {row.original.name} {row.original.last_name}
        </Link>
      ),
    }),
    columnHelper.accessor('rut', {
      header: () => (
        <button onClick={() => handleSort('rut')} className="flex items-center gap-1 hover:text-primary-600">
          RUT <SortIcon columnId="rut" />
        </button>
      ),
    }),
    columnHelper.accessor('email', {
      header: () => (
        <button onClick={() => handleSort('email')} className="flex items-center gap-1 hover:text-primary-600">
          Email <SortIcon columnId="email" />
        </button>
      ),
    }),
    columnHelper.accessor('phone_number', { header: 'Teléfono' }),
    columnHelper.accessor('commune', {
      header: () => (
        <button onClick={() => handleSort('commune')} className="flex items-center gap-1 hover:text-primary-600">
          Comuna <SortIcon columnId="commune" />
        </button>
      ),
    }),
    columnHelper.accessor('created_at', {
      header: () => (
        <button onClick={() => handleSort('created_at')} className="flex items-center gap-1 hover:text-primary-600">
          Fecha Registro <SortIcon columnId="created_at" />
        </button>
      ),
      cell: ({ row }) => formatRegisteredDate(row.original.created_at),
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Acciones',
      size: 120,
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Link
            to={`/clients/${row.original.id}`}
            className="text-primary-600 hover:text-primary-800 transition-colors"
            title="Ver detalle"
          >
            <Eye size={18} />
          </Link>
          <Link
            to={`/clients/${row.original.id}/edit`}
            className="text-primary-600 hover:text-primary-800 transition-colors"
            title="Editar"
          >
            <Pencil size={18} />
          </Link>
        </div>
      ),
    }),
  ]

  const pagination: PaginationState = {
    pageIndex: filters.page - 1,
    pageSize: filters.limit,
  }

  const pageCount = Math.ceil(total / filters.limit)

  const table = useReactTable({
    data,
    columns,
    pageCount,
    state: { pagination },
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === 'function' ? updater(pagination) : updater
      if (newPagination.pageSize !== filters.limit) {
        onLimitChange(newPagination.pageSize)
      }
      if (newPagination.pageIndex + 1 !== filters.page) {
        onPageChange(newPagination.pageIndex + 1)
      }
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  })

  if (isLoading) {
    return <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  }

  return (
    <div>
      {/* Search + Filter Toggle */}
      <div className="p-4 border-b">
        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={localFilters.search}
              onChange={(e) => setLocalFilters((prev) => ({ ...prev, search: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch() }}
              placeholder="Buscar por RUT, Email o Nombre..."
              className="input pl-9"
            />
          </div>
          <button onClick={handleSearch} className="btn btn-primary">
            Buscar
          </button>
          {localFilters.search && (
            <button
              onClick={() => {
                setLocalFilters((prev) => ({ ...prev, search: '' }))
                onApplyFilters({ search: undefined })
              }}
              className="btn btn-secondary"
            >
              <X size={16} />
            </button>
          )}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn ${showFilters || hasActiveFilters ? 'btn-primary' : 'btn-secondary'} flex items-center gap-1.5`}
          >
            <Filter size={16} />
            Filtros
            {hasActiveFilters && (
              <span className="bg-white text-primary-600 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold ml-1">
                !
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="p-4 border-b bg-gray-50">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Sucursal</label>
              <select
                value={localFilters.id_branch_office}
                onChange={(e) => setLocalFilters((prev) => ({ ...prev, id_branch_office: e.target.value }))}
                className="input text-sm"
              >
                <option value="">Todas</option>
                {Object.entries(branchNames).map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Nivel Club</label>
              <select
                value={localFilters.last_known_category}
                onChange={(e) => setLocalFilters((prev) => ({ ...prev, last_known_category: e.target.value }))}
                className="input text-sm"
              >
                <option value="">Todos</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Género</label>
              <select
                value={localFilters.gender}
                onChange={(e) => setLocalFilters((prev) => ({ ...prev, gender: e.target.value }))}
                className="input text-sm"
              >
                <option value="">Todos</option>
                <option value="Masculino">Masculino</option>
                <option value="Femenino">Femenino</option>
                <option value="Otro">Otro</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Registrado desde</label>
              <input
                type="date"
                value={localFilters.created_from}
                onChange={(e) => setLocalFilters((prev) => ({ ...prev, created_from: e.target.value }))}
                className="input text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Registrado hasta</label>
              <input
                type="date"
                value={localFilters.created_to}
                onChange={(e) => setLocalFilters((prev) => ({ ...prev, created_to: e.target.value }))}
                className="input text-sm"
              />
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={handleApplyPanel} className="btn btn-primary text-sm">
              Aplicar Filtros
            </button>
            {hasActiveFilters && (
              <button onClick={handleClear} className="btn btn-secondary text-sm">
                Limpiar filtros
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left border-collapse">
          <thead className="bg-gray-50 border-b">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="px-4 py-3 font-medium text-gray-700">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center text-gray-500">
                  No se encontraron clientes
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-b hover:bg-gray-50 transition-colors">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t">
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            {total} resultado{total !== 1 ? 's' : ''} - Página {filters.page} de {pageCount || 1}
          </span>
          <select
            value={filters.limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
            className="input w-auto text-sm"
          >
            {[10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size} por página
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onPageChange(filters.page - 1)}
            disabled={filters.page <= 1}
            className="btn btn-secondary text-sm disabled:opacity-50"
          >
            Anterior
          </button>
          <button
            onClick={() => onPageChange(filters.page + 1)}
            disabled={filters.page >= pageCount}
            className="btn btn-secondary text-sm disabled:opacity-50"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>
  )
}
